import React, { useMemo, useState } from 'react';
import UsersTab from './UsersTab';
import ActivitiesTab from './ActivitiesTab';
import DashboardTab from './DashboardTab';
import BusinessesTab from './BusinessesTab';
import BusinessSubmissionsTab from './BusinessSubmissionsTab';
import AnnouncementsTab from './AnnouncementsTab';
import BOUsersTab from './BOUsersTab';
import ContactMessagesTab from './ContactMessagesTab';
import NotificationsTab from './NotificationsTab';
import ConfigurationTab from './ConfigurationTab';
import ActivityDaysTab from './ActivityDaysTab';
import BusinessReviewsTab from './BusinessReviewsTab';

import { LogOutIcon, UsersIcon, ActivityIcon, DashboardIcon, StoreIcon, MegaphoneIcon, LeadsIcon, BellIcon, MailIcon, SettingsIcon, CalendarIcon, StarIcon } from '../utils/icons';

// ── ניווט אנכי מקובץ בתיקיות (במקום שורת טאבים אופקית ארוכה) — כל תיקייה
// היא נושא, נפתחת/נסגרת בנפרד, והטאב הפעיל תמיד גלוי כי התיקייה שמכילה
// אותו פתוחה כברירת מחדל. adminOnly על טאב/תיקייה שלמה - מוסתר מ-role='agent'
// ("נציג"), בהתאמה להרשאות שנאכפות גם בשרת (fnIsBOAdmin ב-BO.sql) - נציג
// רואה רק דשבורד וניהול בתי עסק. הודעות/פרסומות פתוח גם לנציג (fnIsBOUser
// ב-spBOGetAnnouncements וכו', BO.sql) - אותה רמת רגישות כמו ניהול בתי עסק ──
const NAV_GROUPS = [
  {
    key: 'overview', label: 'סקירה', emoji: '📊',
    tabs: [
      { key: 'dashboard', label: 'דשבורד', Icon: DashboardIcon },
    ],
  },
  {
    key: 'people', label: 'משתמשים', emoji: '👥', adminOnly: true,
    tabs: [
      { key: 'users', label: 'משתמשים', Icon: UsersIcon },
      { key: 'activities', label: 'פעילויות', Icon: ActivityIcon },
    ],
  },
  {
    key: 'business', label: 'בתי עסק', emoji: '🏪',
    tabs: [
      { key: 'businesses', label: 'בתי עסק', Icon: StoreIcon },
      { key: 'submissions', label: 'הצעות ממתינות', Icon: LeadsIcon },
      { key: 'businessReviews', label: 'דירוגים ותגובות', Icon: StarIcon, adminOnly: true },
    ],
  },
  {
    key: 'marketing', label: 'שיווק', emoji: '📣',
    tabs: [
      { key: 'announcements', label: 'הודעות/פרסומות', Icon: MegaphoneIcon },
      { key: 'notifications', label: 'שליחת התראות', Icon: BellIcon },
      { key: 'contactMessages', label: 'צור קשר', Icon: MailIcon },
    ],
  },
  {
    key: 'system', label: 'מערכת', emoji: '⚙️', adminOnly: true,
    tabs: [
      { key: 'boUsers', label: 'משתמשי בק אופיס', Icon: UsersIcon },
      { key: 'activityDays', label: 'ימי פעילות', Icon: CalendarIcon },
      { key: 'configuration', label: 'קונפיגורציה', Icon: SettingsIcon },
    ],
  },
];

const findGroupKeyForTab = (tabKey) => NAV_GROUPS.find((g) => g.tabs.some((t) => t.key === tabKey))?.key;

const MainPage = ({ currentUser, currentUserRole, onLogout }) => {
  const isAdmin = currentUserRole === 'admin';
  const navGroups = useMemo(() => (
    NAV_GROUPS
      .filter((g) => isAdmin || !g.adminOnly)
      .map((g) => ({ ...g, tabs: g.tabs.filter((t) => isAdmin || !t.adminOnly) }))
  ), [isAdmin]);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [expandedGroups, setExpandedGroups] = useState(() => new Set([findGroupKeyForTab('dashboard')]));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectTab = (tabKey) => {
    setActiveTab(tabKey);
    setMobileNavOpen(false);
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'users': return <UsersTab />;
      case 'dashboard': return <DashboardTab />;
      case 'activities': return <ActivitiesTab />;
      case 'businesses': return <BusinessesTab />;
      case 'submissions': return <BusinessSubmissionsTab />;
      case 'businessReviews': return <BusinessReviewsTab />;
      case 'announcements': return <AnnouncementsTab />;
      case 'notifications': return <NotificationsTab />;
      case 'contactMessages': return <ContactMessagesTab />;
      case 'boUsers': return <BOUsersTab />;
      case 'configuration': return <ConfigurationTab />;
      case 'activityDays': return <ActivityDaysTab />;
      default: return null;
    }
  };

  return (
    <div style={styles.mainContainer}>
      <style>{`
        .bo-hamburger { display: none; }
        .bo-overlay { display: none; }
        @media (max-width: 900px) {
          .bo-hamburger { display: inline-flex !important; }
          .bo-body { flex-direction: column !important; padding: 1rem !important; gap: 12px !important; }
          .bo-tab-content { padding: 1.25rem !important; }
          .bo-sidebar {
            position: fixed;
            top: 0;
            right: -280px;
            width: 260px;
            height: 100vh;
            z-index: 50;
            transition: right 0.25s ease;
            box-shadow: -4px 0 24px rgba(0,0,0,0.18);
          }
          .bo-sidebar.open { right: 0; }
          .bo-overlay.open {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.35);
            z-index: 40;
          }
        }
        @media (max-width: 480px) {
          .bo-header { padding: 0.75rem 1rem !important; }
          .bo-header-title { font-size: 18px !important; }
          .bo-body { padding: 0.75rem !important; }
          .bo-tab-content { padding: 0.85rem !important; }
          .bo-sidebar { width: 80vw !important; right: -80vw; }
          .bo-logout-text { display: none; }
        }
      `}</style>

      {/* Header */}
      <header className="bo-header" style={styles.mainHeader}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <div style={styles.headerTitleRow}>
              <button
                className="bo-hamburger"
                onClick={() => setMobileNavOpen((v) => !v)}
                style={styles.hamburgerBtn}
                aria-label="תפריט"
              >
                ☰
              </button>
              <h1 className="bo-header-title" style={styles.headerTitle}>איגוד - בק אופיס</h1>
            </div>
            <p style={styles.headerWelcome}>ברוכים הבאים, {currentUser}</p>
          </div>
          <button onClick={onLogout} style={styles.logoutBtn}>
            <LogOutIcon />
            <span className="bo-logout-text">התנתק</span>
          </button>
        </div>
      </header>

      <div className="bo-body" style={styles.body}>
        <div className={`bo-overlay ${mobileNavOpen ? 'open' : ''}`} onClick={() => setMobileNavOpen(false)} />

        {/* Sidebar — ניווט אנכי מקובץ בתיקיות */}
        <nav className={`bo-sidebar ${mobileNavOpen ? 'open' : ''}`} style={styles.sidebar}>
          {navGroups.map((group) => {
            const expanded = expandedGroups.has(group.key);
            return (
              <div key={group.key} style={styles.navGroup}>
                <button onClick={() => toggleGroup(group.key)} style={styles.navGroupHeader}>
                  <span style={styles.navGroupEmoji}>{group.emoji}</span>
                  <span style={styles.navGroupLabel}>{group.label}</span>
                  <span style={styles.navGroupChevron}>{expanded ? '▾' : '◂'}</span>
                </button>
                {expanded && (
                  <div style={styles.navGroupTabs}>
                    {group.tabs.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => selectTab(tab.key)}
                        style={{
                          ...styles.navTab,
                          ...(activeTab === tab.key ? styles.navTabActive : {}),
                        }}
                      >
                        <tab.Icon />
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Main Content */}
        <main style={styles.mainContent}>
          <div style={styles.cardContainer}>
            <div className="bo-tab-content" style={styles.tabContent}>
              {renderTab()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const styles = {
  mainContainer: {
    background: "#f6f8fb",
    minHeight: "100vh",
    fontFamily: "system-ui, -apple-system, sans-serif",
    direction: "rtl"
  },

  mainHeader: {
    background: "#ffffff",
    borderBottom: "1px solid #e3e8ee",
    padding: "1rem 2rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)"
  },

  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px"
  },

  headerLeft: {
    display: "flex",
    flexDirection: "column",
    gap: "2px"
  },

  headerTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  hamburgerBtn: {
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    background: "#f6f8fb",
    border: "1px solid #e3e8ee",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "18px",
    color: "#1a1f36",
  },

  headerTitle: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#1a1f36",
    margin: 0
  },

  headerWelcome: {
    fontSize: "13px",
    color: "#697386",
    margin: 0
  },

  logoutBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#fff1f1",
    color: "#d93838",
    border: "1px solid #fbc4c4",
    padding: "8px 14px",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    transition: "all 0.2s ease"
  },

  body: {
    display: "flex",
    alignItems: "flex-start",
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "2rem",
    gap: "24px",
  },

  sidebar: {
    flexShrink: 0,
    width: "260px",
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #e3e8ee",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  navGroup: {
    display: "flex",
    flexDirection: "column",
  },

  navGroupHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "transparent",
    border: "none",
    padding: "10px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "700",
    color: "#1a1f36",
    textAlign: "right",
  },

  navGroupEmoji: {
    fontSize: "16px",
  },

  navGroupLabel: {
    flex: 1,
  },

  navGroupChevron: {
    color: "#8792a2",
    fontSize: "12px",
  },

  navGroupTabs: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingRight: "14px",
    marginBottom: "6px",
  },

  navTab: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "transparent",
    border: "none",
    borderRight: "2px solid transparent",
    padding: "9px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
    color: "#697386",
    textAlign: "right",
    transition: "all 0.15s ease",
  },

  navTabActive: {
    color: "#1f6feb",
    background: "#eef4ff",
    borderRight: "2px solid #1f6feb",
  },

  mainContent: {
    flex: 1,
    minWidth: 0,
  },

  cardContainer: {
    background: "#ffffff",
    borderRadius: "20px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
    border: "1px solid #e3e8ee",
    overflow: "hidden"
  },

  tabContent: {
    padding: "2rem"
  }
};

export default MainPage;
